import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import type { LocalQuestion, QuestionType, FormType, AssessmentType } from '../types';

interface QuestionTypeOption {
  value: QuestionType;
  label: string;
}

interface QuestionModalProps {
  isOpen: boolean;
  editingQuestion: LocalQuestion | null;
  formType: FormType;
  assessmentType: AssessmentType;
  availableTypes: QuestionTypeOption[];
  onSave: (question: LocalQuestion) => void;
  onClose: () => void;
}

const QuestionModal: React.FC<QuestionModalProps> = ({
  isOpen,
  editingQuestion,
  formType,
  assessmentType,
  availableTypes,
  onSave,
  onClose,
}) => {
  const [questionType, setQuestionType] = useState<QuestionType>('short_text');
  const [questionText, setQuestionText] = useState('');
  const [questionRequired, setQuestionRequired] = useState(false);
  const [options, setOptions] = useState<string[]>(['Option 1', 'Option 2']);
  const [correctAnswers, setCorrectAnswers] = useState<number[]>([]);
  const [score, setScore] = useState(1);
  const [sliderMin, setSliderMin] = useState(1);
  const [sliderMax, setSliderMax] = useState(10);

  useEffect(() => {
    if (editingQuestion) {
      setQuestionType(editingQuestion.type);
      setQuestionText(editingQuestion.text);
      setQuestionRequired(editingQuestion.required);
      setOptions(editingQuestion.options || ['Option 1', 'Option 2']);
      setCorrectAnswers(editingQuestion.correctAnswers || []);
      setScore(editingQuestion.score || 1);
      setSliderMin(editingQuestion.sliderMin || 1);
      setSliderMax(editingQuestion.sliderMax || 10);
    } else {
      setQuestionType(availableTypes[0]?.value ?? 'short_text');
      setQuestionText('');
      setQuestionRequired(false);
      setOptions(['Option 1', 'Option 2']);
      setCorrectAnswers([]);
      setScore(1);
      setSliderMin(1);
      setSliderMax(10);
    }
  }, [editingQuestion, availableTypes]);

  const handleAddOption = useCallback(() => {
    setOptions(prev => [...prev, `Option ${prev.length + 1}`]);
  }, []);

  const handleUpdateOption = useCallback((index: number, value: string) => {
    setOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setOptions(prev => {
      if (prev.length <= 2) return prev;
      return prev.filter((_, i) => i !== index);
    });
    setCorrectAnswers(prev =>
      prev.filter(idx => idx !== index).map(idx => (idx > index ? idx - 1 : idx))
    );
  }, []);

  const handleToggleCorrectAnswer = useCallback((index: number) => {
    if (questionType === 'multiple_choice_single') {
      setCorrectAnswers(prev => (prev.includes(index) ? [] : [index]));
    } else {
      setCorrectAnswers(prev =>
        prev.includes(index) ? prev.filter(idx => idx !== index) : [...prev, index]
      );
    }
  }, [questionType]);

  const handleSave = () => {
    if (!questionText.trim()) {
      toast.error('Please enter a question text');
      return;
    }
    if (score < 1) {
      toast.error('Question score must be at least 1');
      return;
    }
    if (questionType.includes('multiple_choice')) {
      const validOptions = options.filter(o => o && o.trim());
      if (validOptions.length < 2) {
        toast.error('Multiple choice questions must have at least 2 options');
        return;
      }
      if (formType === 'assessment' && correctAnswers.length === 0) {
        toast.error('Please mark at least one correct answer for assessment questions');
        return;
      }
    }
    if (questionType === 'slider' && sliderMin >= sliderMax) {
      toast.error('Slider minimum must be less than maximum');
      return;
    }

    const questionData: LocalQuestion = {
      id: editingQuestion?.id || `temp-${Date.now()}`,
      type: questionType,
      text: questionText.trim(),
      required: questionRequired,
      options: questionType.includes('multiple_choice') ? options.filter(o => o.trim()) : undefined,
      correctAnswers: (formType === 'assessment' && questionType.includes('multiple_choice')) ? correctAnswers : undefined,
      score,
      sliderMin: questionType === 'slider' ? sliderMin : undefined,
      sliderMax: questionType === 'slider' ? sliderMax : undefined,
    };

    onSave(questionData);
  };

  if (!isOpen) return null;

  return (
    <div className="admin-feedback-modal-overlay" onClick={onClose}>
      <div className="admin-feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="admin-feedback-modal-header">
          <h3>{editingQuestion ? 'Edit Question' : 'Add Question'}</h3>
          <button className="admin-feedback-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="admin-feedback-modal-body">
          <div className="admin-feedback-form-field">
            <label>Question Type</label>
            {formType === 'assessment' && (
              <div className="admin-feedback-info-message" style={{ marginBottom: '8px', padding: '8px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '13px', color: '#92400e' }}>
                {assessmentType === 'both'
                  ? 'Both objective (multiple choice) and subjective (text, slider) questions are available'
                  : assessmentType === 'subjective'
                  ? 'Subjective assessments support text and slider questions'
                  : 'Assessment forms can only contain multiple choice questions'}
              </div>
            )}
            <div className="admin-feedback-question-type-grid">
              {availableTypes.map((type) => (
                <button
                  key={type.value}
                  className={`admin-feedback-question-type-btn ${questionType === type.value ? 'active' : ''}`}
                  onClick={() => setQuestionType(type.value)}
                >
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="admin-feedback-form-field">
            <label>Question Text *</label>
            <textarea
              rows={3}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Enter your question..."
            />
          </div>

          {questionType.includes('multiple_choice') && (
            <div className="admin-feedback-form-field">
              <label>
                Options{' '}
                {formType === 'assessment' && (
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                    ({questionType === 'multiple_choice_single' ? 'Select ONE correct answer' : 'Check correct answer(s)'})
                  </span>
                )}
              </label>
              <div className="admin-feedback-options-list">
                {options.map((option, index) => (
                  <div key={index} className="admin-feedback-option-row">
                    {formType === 'assessment' && (
                      <input
                        type={questionType === 'multiple_choice_single' ? 'radio' : 'checkbox'}
                        name={questionType === 'multiple_choice_single' ? 'correct-answer' : undefined}
                        checked={correctAnswers.includes(index)}
                        onChange={() => handleToggleCorrectAnswer(index)}
                        style={{ width: 'auto', marginRight: '8px', cursor: 'pointer' }}
                        title="Mark as correct answer"
                      />
                    )}
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleUpdateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      style={{ flex: 1 }}
                    />
                    {options.length > 2 && (
                      <button
                        className="admin-feedback-remove-option-btn"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button className="admin-feedback-add-option-btn" onClick={handleAddOption}>
                  <Plus size={14} /> Add Option
                </button>
              </div>
              {formType === 'assessment' && correctAnswers.length > 0 && (
                <div style={{ marginTop: '8px', padding: '8px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px', fontSize: '13px', color: '#166534' }}>
                  {correctAnswers.length} correct answer{correctAnswers.length > 1 ? 's' : ''} marked
                </div>
              )}
            </div>
          )}

          <div className="admin-feedback-form-field">
            <label className="admin-feedback-checkbox-label">
              <input
                type="checkbox"
                checked={questionRequired}
                onChange={(e) => setQuestionRequired(e.target.checked)}
              />
              <span>Required question</span>
            </label>
          </div>

          <div className="admin-feedback-form-field admin-feedback-question-score-row">
            <label>Question Score</label>
            <input
              type="number"
              min={1}
              value={score}
              onChange={(e) => setScore(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: '80px' }}
            />
          </div>

          {questionType === 'slider' && (
            <div className="admin-feedback-form-field admin-feedback-slider-config">
              <label>Slider Range</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={sliderMin}
                  onChange={(e) => setSliderMin(parseInt(e.target.value) || 1)}
                  style={{ width: '70px' }}
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={sliderMax}
                  onChange={(e) => setSliderMax(parseInt(e.target.value) || 10)}
                  style={{ width: '70px' }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="admin-feedback-modal-footer">
          <button
            className="admin-feedback-modal-btn admin-feedback-modal-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="admin-feedback-modal-btn admin-feedback-modal-btn-primary"
            onClick={handleSave}
            disabled={!questionText.trim()}
          >
            {editingQuestion ? 'Update Question' : 'Add Question'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(QuestionModal);
